/**
 * Suvinil - Client Routes
 *
 * Rotas específicas do cliente Suvinil.
 * Importadas condicionalmente pelo App.tsx quando VITE_CLIENT_CODE=suvinil.
 */

import { lazy, Suspense } from 'react';
import { Route } from 'react-router-dom';
import { Spinner } from '@/components/ui/Spinner';

// Lazy-loaded pages
const SuvinilDashboard = lazy(() => import('./pages/dashboards/SuvinilDashboard'));

// Book
const BookList = lazy(() => import('./pages/book/BookList'));
const BookDetail = lazy(() => import('./pages/book/BookDetail'));

// CC
const CCPotencialList = lazy(() => import('./pages/cc/CCPotencialList'));
const CCFiscalSimulator = lazy(() => import('./pages/cc/CCFiscalSimulator'));
const CCLicitacoesList = lazy(() => import('./pages/cc/CCLicitacoesList'));

// AO
const AOList = lazy(() => import('./pages/ao/AOList'));
const AODetail = lazy(() => import('./pages/ao/AODetail'));

// Potencial
const PotencialList = lazy(() => import('./pages/potencial/PotencialList'));

// MOPA / ProSelf
const MOPAList = lazy(() => import('./pages/mopa/MOPAList'));
const MOPASimulator = lazy(() => import('./pages/mopa/MOPASimulator'));
const ProSelfList = lazy(() => import('./pages/mopa/ProSelfList'));

// PDV Ideal
const PDVIdealList = lazy(() => import('./pages/pdvIdeal/PDVIdealList'));

// Marketing / Budget AR
const MarketingInvestList = lazy(() => import('./pages/marketing/MarketingInvestList'));
const BudgetARTree = lazy(() => import('./pages/marketing/BudgetARTree'));

// SellOut / Sou Mais
const SellOutList = lazy(() => import('./pages/sellout/SellOutList'));
const SouMaisList = lazy(() => import('./pages/sellout/SouMaisList'));

// Treinamentos
const TrainingCalendar = lazy(() => import('./pages/treinamentos/TrainingCalendar'));
const TrainingPortal = lazy(() => import('./pages/treinamentos/TrainingPortal'));

// SAC / Pagamentos
const SACList = lazy(() => import('./pages/sac/SACList'));
const PagamentosList = lazy(() => import('./pages/sac/PagamentosList'));

// Analytics (Superset)
const SupersetPage = lazy(() => import('./pages/dashboards/SupersetPage'));

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<div className="flex justify-center py-12"><Spinner /></div>}>{children}</Suspense>;
}

/**
 * Rotas do cliente Suvinil.
 * Todas sob o prefixo /suvinil/
 */
export const suvinilRoutes = (
  <>
    {/* Dashboard */}
    <Route path="/suvinil" element={<Lazy><SuvinilDashboard /></Lazy>} />

    {/* Book */}
    <Route path="/suvinil/book" element={<Lazy><BookList /></Lazy>} />
    <Route path="/suvinil/book/:id" element={<Lazy><BookDetail /></Lazy>} />

    {/* Construção Civil */}
    <Route path="/suvinil/cc/potencial" element={<Lazy><CCPotencialList /></Lazy>} />
    <Route path="/suvinil/cc/fiscal" element={<Lazy><CCFiscalSimulator /></Lazy>} />
    <Route path="/suvinil/cc/licitacoes" element={<Lazy><CCLicitacoesList /></Lazy>} />

    {/* Acordos de Objetivos */}
    <Route path="/suvinil/ao" element={<Lazy><AOList /></Lazy>} />
    <Route path="/suvinil/ao/:id" element={<Lazy><AODetail /></Lazy>} />

    {/* Potencial de Mercado */}
    <Route path="/suvinil/potencial" element={<Lazy><PotencialList /></Lazy>} />

    {/* MOPA & ProSelf */}
    <Route path="/suvinil/mopa" element={<Lazy><MOPAList /></Lazy>} />
    <Route path="/suvinil/mopa/simulator" element={<Lazy><MOPASimulator /></Lazy>} />
    <Route path="/suvinil/proself" element={<Lazy><ProSelfList /></Lazy>} />

    {/* PDV Ideal */}
    <Route path="/suvinil/pdv-ideal" element={<Lazy><PDVIdealList /></Lazy>} />

    {/* Marketing & Budget */}
    <Route path="/suvinil/marketing/invest" element={<Lazy><MarketingInvestList /></Lazy>} />
    <Route path="/suvinil/marketing/budget" element={<Lazy><BudgetARTree /></Lazy>} />

    {/* SellOut & Sou Mais */}
    <Route path="/suvinil/sellout/contracts" element={<Lazy><SellOutList /></Lazy>} />
    <Route path="/suvinil/sellout/sou-mais" element={<Lazy><SouMaisList /></Lazy>} />

    {/* Treinamentos */}
    <Route path="/suvinil/treinamentos" element={<Lazy><TrainingCalendar /></Lazy>} />
    <Route path="/suvinil/treinamentos/portal/:code" element={<Lazy><TrainingPortal /></Lazy>} />

    {/* SAC & Pagamentos */}
    <Route path="/suvinil/sac/fichas" element={<Lazy><SACList /></Lazy>} />
    <Route path="/suvinil/sac/pagamentos" element={<Lazy><PagamentosList /></Lazy>} />

    {/* Analytics (Superset) */}
    <Route path="/suvinil/analytics" element={<Lazy><SupersetPage /></Lazy>} />
  </>
);
